import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Facebook, Linkedin, Link2, RefreshCw, Loader2, CheckCircle2,
  AlertTriangle, Plug, DollarSign, MousePointerClick, Eye, Target,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => (n || 0).toLocaleString();

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A` }}>
          <Icon className="w-4 h-4" style={{ color: tone }} />
        </div>
        <div>
          <div className="text-lg font-semibold text-[#1A1A1A]">{value}</div>
          <div className="text-[11px] text-[#8A857A]">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformCard({ name, icon: Icon, tone, cfg, onConnect, onRefresh, busy }) {
  const connected = cfg?.connected;
  const configured = cfg?.configured;
  return (
    <div className="rounded-xl border border-[#DAD5CA] bg-[#FFFFFF] p-4" data-testid={`ad-platform-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A` }}>
            <Icon className="w-5 h-5" style={{ color: tone }} />
          </div>
          <div>
            <div className="text-[#1A1A1A] font-medium">{name}</div>
            <div className="text-[11px] flex items-center gap-1.5">
              {!configured ? (
                <span className="text-[#8A857A] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Not configured</span>
              ) : connected ? (
                <span className="text-[#0E6B3F] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected · {cfg.account_id}</span>
              ) : (
                <span className="text-[#6B5600] inline-flex items-center gap-1"><Plug className="w-3 h-3" /> Needs authorization</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {configured && !connected && onConnect && (
            <Button size="sm" onClick={onConnect} className="text-[#1A1A1A] font-medium" style={{ background: tone }} data-testid={`ad-connect-${name.toLowerCase()}`}>
              <Link2 className="w-4 h-4 mr-1" /> Connect
            </Button>
          )}
          {configured && connected && (
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid={`ad-refresh-${name.toLowerCase()}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
            </Button>
          )}
        </div>
      </div>
      {!configured && (
        <p className="text-[11px] text-[#8A857A] mt-3">Add {name} API credentials in the backend to enable live sync.</p>
      )}
    </div>
  );
}

export default function AdConnectors() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, sum] = await Promise.all([
        axios.get(`${API}/marketing/ads/status`),
        axios.get(`${API}/marketing/ads/summary?days=90`),
      ]);
      setStatus(s.data);
      setSummary(sum.data);
    } catch (e) {
      toast.error("Failed to load ad connectors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-check status when returning from the LinkedIn OAuth popup.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const connectLinkedIn = async () => {
    try {
      const r = await axios.get(`${API}/marketing/ads/linkedin/start`);
      window.open(r.data.authorize_url, "linkedin_oauth", "width=600,height=740");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start LinkedIn connect");
    }
  };

  const refresh = async (platform) => {
    setBusy(platform);
    try {
      const r = await axios.post(`${API}/marketing/ads/${platform}/refresh`, {});
      toast.success(`${platform === "meta" ? "Meta" : "LinkedIn"}: synced ${r.data.rows} row(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setBusy(""); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-[#8A857A] py-8" data-testid="ad-connectors-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading ad platforms…</div>;
  }

  const t = summary?.totals || {};
  const campaigns = summary?.campaigns || [];

  return (
    <div className="space-y-5" data-testid="ad-connectors">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#1A1A1A]">Ad Platforms</h3>
          <p className="text-xs text-[#59554D]">Live spend &amp; performance pulled straight from your ad accounts (last 90 days)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PlatformCard name="Meta" icon={Facebook} tone="#8A6D00" cfg={status?.meta}
          onRefresh={() => refresh("meta")} busy={busy === "meta"} />
        <PlatformCard name="LinkedIn" icon={Linkedin} tone="#0A66C2" cfg={status?.linkedin}
          onConnect={connectLinkedIn} onRefresh={() => refresh("linkedin")} busy={busy === "linkedin"} />
      </div>

      {/* Rolled-up metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Metric icon={DollarSign} label="Spend" value={fmtMoney(t.spend)} tone="#0E6B3F" />
        <Metric icon={Eye} label="Impressions" value={fmtNum(t.impressions)} tone="#1D4ED8" />
        <Metric icon={MousePointerClick} label="Clicks" value={fmtNum(t.clicks)} tone="#8A6D00" />
        <Metric icon={Target} label="Conversions" value={fmtNum(t.conversions)} tone="#6B5600" />
        <Metric icon={DollarSign} label="CPC" value={fmtMoney(t.cpc)} tone="#3B82F6" />
        <Metric icon={DollarSign} label="Cost / Lead" value={fmtMoney(t.cpl)} tone="#C23A00" />
      </div>

      {/* Per-campaign breakdown */}
      {campaigns.length > 0 ? (
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-0">
            <Table data-testid="ad-campaigns-table">
              <TableHeader>
                <TableRow className="border-[#DAD5CA] hover:bg-transparent">
                  <TableHead className="text-[#8A857A]">Campaign</TableHead>
                  <TableHead className="text-[#8A857A]">Platform</TableHead>
                  <TableHead className="text-[#8A857A] text-right">Spend</TableHead>
                  <TableHead className="text-[#8A857A] text-right">Impr.</TableHead>
                  <TableHead className="text-[#8A857A] text-right">Clicks</TableHead>
                  <TableHead className="text-[#8A857A] text-right">CTR</TableHead>
                  <TableHead className="text-[#8A857A] text-right">CPC</TableHead>
                  <TableHead className="text-[#8A857A] text-right">Conv.</TableHead>
                  <TableHead className="text-[#8A857A] text-right">CPL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c, i) => (
                  <TableRow key={`${c.platform}-${c.campaign_id}-${i}`} className="border-[#F4F2ED]" data-testid={`ad-campaign-row-${i}`}>
                    <TableCell className="text-[#1A1A1A] max-w-[280px] truncate">{c.campaign_name || c.campaign_id}</TableCell>
                    <TableCell>
                      <Badge className="text-[10px] border-0" style={{ background: c.platform === "meta" ? "#8A6D0022" : "#0A66C222", color: c.platform === "meta" ? "#8A6D00" : "#0A66C2" }}>
                        {c.platform === "meta" ? "Meta" : "LinkedIn"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-[#1A1A1A]">{fmtMoney(c.spend)}</TableCell>
                    <TableCell className="text-right text-[#59554D]">{fmtNum(c.impressions)}</TableCell>
                    <TableCell className="text-right text-[#59554D]">{fmtNum(c.clicks)}</TableCell>
                    <TableCell className="text-right text-[#59554D]">{c.ctr}%</TableCell>
                    <TableCell className="text-right text-[#59554D]">{fmtMoney(c.cpc)}</TableCell>
                    <TableCell className="text-right text-[#59554D]">{fmtNum(c.conversions)}</TableCell>
                    <TableCell className="text-right text-[#59554D]">{fmtMoney(c.cpl)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-dashed border-[#DAD5CA] bg-[#FFFFFF] p-6 text-center" data-testid="ad-campaigns-empty">
          <p className="text-sm text-[#59554D]">No campaign data yet.</p>
          <p className="text-xs text-[#8A857A] mt-1">Connect a platform above and hit Refresh to pull your latest campaigns.</p>
        </div>
      )}
    </div>
  );
}
